import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Checkbox,
  Grid,
  Card,
  CardContent
} from '@mui/material'
import {
  Delete,
  Download,
  Archive,
  Refresh,
  FilterList,
  Visibility,
  DeleteSweep
} from '@mui/icons-material'
import axios from 'axios'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

const LogsManagement = () => {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [selectedLogs, setSelectedLogs] = useState([])
  const [openArchiveDialog, setOpenArchiveDialog] = useState(false)
  const [daysOld, setDaysOld] = useState(90)
  const [stats, setStats] = useState(null)

  // Filtros
  const [filters, setFilters] = useState({
    tabla: '',
    usuario_id: '',
    accion: '',
    fecha_inicio: '',
    fecha_fin: ''
  })
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    loadLogs()
    loadStats()
  }, [page, rowsPerPage])

  const loadLogs = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('sgc_token')

      // Solo agregar parámetros con valores
      const params = new URLSearchParams({
        limit: rowsPerPage,
        offset: page * rowsPerPage
      })

      // Agregar filtros solo si tienen valor
      if (filters.tabla) params.append('tabla', filters.tabla)
      if (filters.usuario_id) params.append('usuario_id', filters.usuario_id)
      if (filters.accion) params.append('accion', filters.accion)
      if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio)
      if (filters.fecha_fin) params.append('fecha_fin', filters.fecha_fin)

      const response = await axios.get(`/api/auditoria?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setLogs(response.data.logs)
    } catch (error) {
      setError('Error al cargar logs: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get('/api/auditoria/stats', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setStats(response.data)
    } catch (error) {
      console.error('Error al cargar estadísticas:', error)
    }
  }

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const params = new URLSearchParams()

      // Solo agregar filtros si tienen valor
      if (filters.tabla) params.append('tabla', filters.tabla)
      if (filters.usuario_id) params.append('usuario_id', filters.usuario_id)
      if (filters.accion) params.append('accion', filters.accion)
      if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio)
      if (filters.fecha_fin) params.append('fecha_fin', filters.fecha_fin)

      const response = await axios.get(`/api/auditoria/export/csv?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `auditoria_logs_${new Date().toISOString()}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()

      setSuccess('Logs exportados exitosamente')
    } catch (error) {
      setError('Error al exportar logs: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleArchiveLogs = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.post('/api/auditoria/archive',
        { days_old: parseInt(daysOld) },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      setSuccess(`${response.data.archived_count} logs archivados exitosamente`)
      setOpenArchiveDialog(false)
      loadLogs()
      loadStats()
    } catch (error) {
      setError('Error al archivar logs: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('¿Estás seguro de eliminar este log? Esta acción no se puede deshacer.')) {
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      await axios.delete(`/api/auditoria/${logId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setSuccess('Log eliminado exitosamente')
      loadLogs()
    } catch (error) {
      setError('Error al eliminar log: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleBulkDelete = async () => {
    if (selectedLogs.length === 0) {
      setError('Selecciona al menos un log para eliminar')
      return
    }

    if (!window.confirm(`¿Estás seguro de eliminar ${selectedLogs.length} logs? Esta acción no se puede deshacer.`)) {
      return
    }

    try {
      const token = localStorage.getItem('sgc_token')
      await axios.post('/api/auditoria/bulk-delete',
        { audit_ids: selectedLogs },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      setSuccess(`${selectedLogs.length} logs eliminados exitosamente`)
      setSelectedLogs([])
      loadLogs()
    } catch (error) {
      setError('Error al eliminar logs: ' + (error.response?.data?.detail || error.message))
    }
  }

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLogs(logs.map(log => log.id))
    } else {
      setSelectedLogs([])
    }
  }

  const handleSelectLog = (logId) => {
    setSelectedLogs(prev => {
      if (prev.includes(logId)) {
        return prev.filter(id => id !== logId)
      } else {
        return [...prev, logId]
      }
    })
  }

  const getAccionColor = (accion) => {
    switch (accion) {
      case 'CREATE':
      case 'INSERT':
        return 'success'
      case 'UPDATE':
        return 'info'
      case 'DELETE':
        return 'error'
      case 'LOGIN':
        return 'primary'
      case 'LOGIN_FAILED':
        return 'error'
      case 'PASSWORD_RESET':
        return 'warning'
      default:
        return 'default'
    }
  }

  const formatFecha = (fecha) => {
    if (!fecha) return ''
    try {
      return format(new Date(fecha), "dd/MM/yyyy HH:mm:ss", { locale: es })
    } catch (error) {
      return fecha
    }
  }

  const applyFilters = () => {
    setPage(0)
    loadLogs()
  }

  const clearFilters = () => {
    setFilters({
      tabla: '',
      usuario_id: '',
      accion: '',
      fecha_inicio: '',
      fecha_fin: ''
    })
    setPage(0)
    setTimeout(() => loadLogs(), 100)
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Gestión de Logs de Auditoría
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refrescar">
            <IconButton onClick={() => { loadLogs(); loadStats(); }} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            variant="outlined"
          >
            Filtros
          </Button>
          <Button
            startIcon={<Download />}
            onClick={handleExportCSV}
            variant="contained"
            color="primary"
          >
            Exportar CSV
          </Button>
          <Button
            startIcon={<Archive />}
            onClick={() => setOpenArchiveDialog(true)}
            variant="contained"
            color="secondary"
          >
            Archivar
          </Button>
          {selectedLogs.length > 0 && (
            <Button
              startIcon={<DeleteSweep />}
              onClick={handleBulkDelete}
              variant="contained"
              color="error"
            >
              Eliminar ({selectedLogs.length})
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Estadísticas */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary">{stats.general_stats.total_events}</Typography>
                <Typography variant="body2" color="text.secondary">Total Eventos</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="success.main">{stats.general_stats.creates}</Typography>
                <Typography variant="body2" color="text.secondary">Creaciones</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="info.main">{stats.general_stats.updates}</Typography>
                <Typography variant="body2" color="text.secondary">Actualizaciones</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="error.main">{stats.general_stats.deletes}</Typography>
                <Typography variant="body2" color="text.secondary">Eliminaciones</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filtros */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>Filtros</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Tabla"
                value={filters.tabla}
                onChange={(e) => setFilters({ ...filters, tabla: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Acción</InputLabel>
                <Select
                  value={filters.accion}
                  onChange={(e) => setFilters({ ...filters, accion: e.target.value })}
                  label="Acción"
                >
                  <MenuItem value="">Todas</MenuItem>
                  <MenuItem value="CREATE">CREATE</MenuItem>
                  <MenuItem value="UPDATE">UPDATE</MenuItem>
                  <MenuItem value="DELETE">DELETE</MenuItem>
                  <MenuItem value="LOGIN">LOGIN</MenuItem>
                  <MenuItem value="LOGOUT">LOGOUT</MenuItem>
                  <MenuItem value="PASSWORD_RESET">PASSWORD_RESET</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Fecha Inicio"
                value={filters.fecha_inicio}
                onChange={(e) => setFilters({ ...filters, fecha_inicio: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Fecha Fin"
                value={filters.fecha_fin}
                onChange={(e) => setFilters({ ...filters, fecha_fin: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={applyFilters} variant="contained">Aplicar Filtros</Button>
                <Button onClick={clearFilters} variant="outlined">Limpiar Filtros</Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Tabla de logs */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedLogs.length === logs.length && logs.length > 0}
                  indeterminate={selectedLogs.length > 0 && selectedLogs.length < logs.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Usuario</TableCell>
              <TableCell>Tabla</TableCell>
              <TableCell>Registro ID</TableCell>
              <TableCell>Acción</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">Cargando logs...</TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">No hay logs disponibles</TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id} selected={selectedLogs.includes(log.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedLogs.includes(log.id)}
                      onChange={() => handleSelectLog(log.id)}
                    />
                  </TableCell>
                  <TableCell>{log.id}</TableCell>
                  <TableCell>{formatFecha(log.created_at)}</TableCell>
                  <TableCell>{log.usuario?.nombre || 'N/A'}</TableCell>
                  <TableCell>{log.tabla}</TableCell>
                  <TableCell>{log.registro_id}</TableCell>
                  <TableCell>
                    <Chip
                      label={log.accion}
                      color={getAccionColor(log.accion)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{log.ip_address || 'N/A'}</TableCell>
                  <TableCell>
                    <Tooltip title="Ver detalle">
                      <IconButton size="small">
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteLog(log.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={-1}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas por página"
        />
      </TableContainer>

      {/* Dialog de Archivar */}
      <Dialog open={openArchiveDialog} onClose={() => setOpenArchiveDialog(false)}>
        <DialogTitle>Archivar Logs Antiguos</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Los logs más antiguos que el número de días especificado serán movidos a una tabla de archivo
            y eliminados de la tabla principal para optimizar el rendimiento.
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Días de antigüedad"
            value={daysOld}
            onChange={(e) => setDaysOld(e.target.value)}
            helperText="Logs con más de estos días serán archivados"
            InputProps={{ inputProps: { min: 1 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenArchiveDialog(false)}>Cancelar</Button>
          <Button onClick={handleArchiveLogs} variant="contained" color="secondary">
            Archivar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default LogsManagement
